import { useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  GroupResponse,
  OrganisationResponse,
  TeamVisibility,
  useAddTeam,
  useAddToExistingTeam,
} from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Box, styled } from '@mui/material';
import { User } from 'types/types';
import OrganisationInfoCard from 'components/OrganisationInfoCard/OrganisationInfoCard';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import {
  SectionTitle,
  SvgMultipleUsers2Bold,
} from '@insights-ltd/design-library/components';
import {
  AddToTeamOptions,
  AddToTeamType,
  NewTeamFields,
} from './AddToTeamHelperComponents';
import { ExistingTeamFields } from './HelperComponents/ExistingTeamFields';

type AddToTeamFormProps = {
  group: GroupResponse | null;
  organisation: OrganisationResponse;
  learners: User[];
};

export type TeamFormData = {
  name: string;
  learners: User[];
  visibility: TeamVisibility;
  organisationId: string;
  selectedTeam: string;
};

export const StyledTeamIcon = styled(SvgMultipleUsers2Bold)(({ theme }) => ({
  fill: theme.palette.blue.main,
  height: '20px',
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& > * + *': {
    marginTop: theme.spacing(spacingSizeMap.M),
  },
}));

const StyledOrganisationIcon = styled(OrganisationIcon)(({ theme }) => ({
  fill: theme.palette.orange.main,
  height: '20px',
}));

const AddToTeamForm = ({
  group,
  organisation,
  learners,
}: AddToTeamFormProps) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TeamFormData>({
    defaultValues: { visibility: 'PRIVATE', organisationId: organisation.id },
  });
  const [getAddToTeamType, setAddToTeamType] =
    useState<AddToTeamType>('new-team');
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedVisibility, setSelectedVisibility] =
    useState<TeamVisibility>('PRIVATE');
  const { openErrorModal } = useRequestErrorContext();

  const { mutate: addTeam, isPending: isAdding } = useAddTeam(queryClient);
  const { mutate: addToExistingTeam, isPending: isAddToExistingTeamPending } =
    useAddToExistingTeam(queryClient);

  const onSubmit = (formData: TeamFormData) => {
    let formDataChecked = { ...formData };
    formDataChecked = {
      ...formDataChecked,
      learners,
      visibility: selectedVisibility,
    };
    if (getAddToTeamType === 'existing-team') {
      addToExistingTeam(
        {
          teamId: formData.selectedTeam,
          learnerIds: learners.map((learner) => learner.id),
        },
        {
          onSuccess: (newTeam) => navigate(`/teams/${newTeam.id}`),
          onError: () => {
            openErrorModal();
          },
        },
      );
    } else {
      addTeam(
        {
          organisationId: organisation.id,
          formData: formDataChecked,
        },
        {
          onSuccess: (newTeam) => navigate(`/teams/${newTeam.id}/created`),
          onError: () => {
            openErrorModal();
          },
        },
      );
    }
  };

  const handleVisibilityChange = (value: TeamVisibility) => {
    setSelectedVisibility(value);
  };

  const resetTeamFields = () => {
    setValue('selectedTeam', '');
  };

  const submitButtonText =
    getAddToTeamType === 'new-team'
      ? 'ui.event-management.teams.create-team'
      : 'ui.event-management.add-to-team.button';

  const addToTeamOptions = {
    'new-team': (
      <NewTeamFields
        errors={errors}
        register={register}
        selectedVisibility={selectedVisibility}
        handleVisibilityChange={handleVisibilityChange}
      />
    ),
    'existing-team': (
      <ExistingTeamFields
        error={errors.selectedTeam}
        register={register}
        organisationId={organisation.id}
        watch={watch}
        setValue={setValue}
      />
    ),
  };

  return (
    <StyledForm noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Box
          sx={{
            mb: spacingSizeMap.L,
          }}
        >
          <SectionTitle
            icon={{
              icon: <StyledOrganisationIcon />,
              iconColour: 'orange',
            }}
            text={t('ui.event-management.users.create.organisation', {
              count: 1,
            })}
          />
        </Box>
        <OrganisationInfoCard
          key={organisation.id}
          id={organisation.id}
          name={organisation.name}
          group={group ?? undefined}
          icon={null}
        />
      </div>
      <Box
        sx={{
          mt: spacingSizeMap.L,
        }}
      >
        <AddToTeamOptions
          setTeamType={(value) => {
            setAddToTeamType(value);
            resetTeamFields();
          }}
          teamType={getAddToTeamType}
        />
      </Box>
      <Box
        sx={{
          mt: spacingSizeMap.XL,
        }}
      >
        <SectionTitle
          icon={{
            icon: <StyledTeamIcon />,
            iconColour: 'blue',
          }}
          text={t('ui.event-management.teams.team-info')}
        />
      </Box>
      {addToTeamOptions[getAddToTeamType]}
      {/* Submit button */}
      <Grid
        container
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Button
          data-testid="add-to-team"
          type="submit"
          variant="contained"
          color="primary"
          disabled={isAdding || isAddToExistingTeamPending}
          endIcon={
            isAdding && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )
          }
        >
          {t(submitButtonText)}
        </Button>
      </Grid>
    </StyledForm>
  );
};

export default AddToTeamForm;
